import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import {
  ACTIONS_HEADER_BTN_SX,
} from "../../../theme/buttonstyles";
import TradeShowWorkspaceSelector from "./TradeShowWorkspaceSelector";
import WorkspacesTable from "./WorkspacesTable";
import { useTradeShowWorkspaces } from "./TradeShowWorkspacesContext";
import BackButton from "../../../shared/components/BackButton";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: `1.5px solid ${BRAND_BLUE}`,
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

export default function TradeShowAgentPage() {
  const { workspaces, addWorkspace, deleteWorkspace } = useTradeShowWorkspaces();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");

  const selectedCount = selectedIds.size;

  async function handleCreateFromTradeShow(data) {
    const workspace = await addWorkspace(data);
    navigate(`/trade-show-agent/workspaces/${workspace.id}`);
    return workspace;
  }

  async function handleDeleteSelected() {
    setIsDeleting(true);
    setError("");
    try {
      await Promise.all([...selectedIds].map((id) => deleteWorkspace(id)));
      setSelectedIds(new Set());
      setDeleteDialogOpen(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Unable to delete the selected workspaces.");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 6 }}>
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: { xs: 2, md: 3 },
            py: 1.75,
            position: "relative",
          }}
        >
          <BackButton to="/tradeshows" label="Back" />
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_BLUE,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <SmartToyIcon sx={{ fontSize: 19 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 800,
                color: BRAND_GREEN,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              Trade Show Agent
            </Typography>
          </Box>
          <Button
            startIcon={<DeleteForeverIcon />}
            disabled={selectedCount === 0}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{ ...ACTIONS_HEADER_BTN_SX, ml: "auto" }}
          >
            {`Delete Selected${selectedCount ? ` (${selectedCount})` : ""}`}
          </Button>
        </Box>
      </Box>

      {/* Page Content */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TradeShowWorkspaceSelector onCreateWorkspace={handleCreateFromTradeShow} />

        {/* Workspaces Table */}
        <Card sx={CARD_SX}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderBottom: "1px solid rgba(0,51,108,0.08)",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "0.95rem" }}>
                Workspaces
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#8a9ab0", mt: 0.25 }}>
                {workspaces.length === 0
                  ? "No workspaces yet"
                  : `${workspaces.length} workspace${workspaces.length !== 1 ? "s" : ""}`}
              </Typography>
            </Box>
          </Box>
          <CardContent sx={{ p: "0 !important" }}>
            <WorkspacesTable
              workspaces={workspaces}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
            />
          </CardContent>
        </Card>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete selected workspaces?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#5a6a80" }}>
            This permanently deletes {selectedCount} selected workspace{selectedCount === 1 ? "" : "s"}, including their exhibitor lists, briefs, drafts, and run history.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteSelected} disabled={isDeleting}>
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
